import { votesApi } from "@/frontend/service/votes/productv2-vote";
import {
	fetchProductRating,
	fetchReviews,
	fetchProductComments,
} from "@/modules/productv2/productv2";

import { TRatingData } from "@/types/productv2";
import { useQuery } from "@tanstack/react-query";

export const useProductEngagement = (productSlug: string) => {
	const { data: ratingData, isLoading: ratingLoading } = useQuery<TRatingData>({
		queryKey: ["productRating", productSlug],
		queryFn: () => fetchProductRating(productSlug),
		enabled: !!productSlug,
		placeholderData: {
			totalRating: "0",
			noOfRatings: 0,
			supportRating: "0",
			functionalityRating: "0",
			valueRating: "0",
			ratingCounts: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
		},
	});

	const { data: reviews = [], isLoading: reviewsLoading } = useQuery({
		queryKey: ["reviews", productSlug],
		queryFn: () => fetchReviews(productSlug),
		enabled: !!productSlug,
	});

	const { data: commentsResponse, isLoading: commentsLoading } = useQuery({
		queryKey: ["product-comments-count", productSlug],
		queryFn: () => fetchProductComments({ productSlug, pageParam: 1, perPageParam: 1 }),
		enabled: !!productSlug,
	});

	const commentsCount = commentsResponse?.pagination?.total || 0;

	const { data: votesData, isLoading: votesLoading } = useQuery({
		queryKey: ["votes", productSlug],
		queryFn: () => votesApi.getProductVotesCount(productSlug),
		staleTime: 5 * 60 * 1000,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

	return {
		rating: ratingData?.totalRating ?? "0",
		reviewsCount: reviews.length,
		isLoading: ratingLoading || reviewsLoading || commentsLoading || votesLoading,
		commentsCount,
		votesData,
	};
};
